import { useMemo, useState } from "react";
import type { CrudColumn, CrudRow } from "../types";

// ---------------------------------------------------------------------------
// Lightweight, self-contained table for the Cargo Market scaffold. Keeps its
// own pagination state so the independent tables across sections don't fight
// over one global store. Visual style (primary header, striped rows, dark mode)
// matches the rest of the app (mirrors the Nuvex Access / CRM DataTable).
// ---------------------------------------------------------------------------

interface DataTableProps<T extends CrudRow> {
  rows: T[];
  columns: CrudColumn<T>[];
  emptyText?: string;
  pageSize?: number;
  /** Optional row actions column (edit/delete) rendered at the end. */
  renderActions?: (row: T) => React.ReactNode;
  actionsLabel?: string;
}

export default function DataTable<T extends CrudRow>({
  rows,
  columns,
  emptyText = "Sin registros",
  pageSize = 8,
  renderActions,
  actionsLabel = "Acciones",
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const pageRows = useMemo(
    () => rows.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [rows, safePage, pageSize]
  );

  const cellAlign = (a?: string) =>
    a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left";

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
        <thead className="bg-primary-500 text-on-primary">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{ width: c.width }}
                className={`p-3 font-bold ${cellAlign(c.align)}`}
              >
                {c.label}
              </th>
            ))}
            {renderActions && (
              <th className="p-3 text-center font-bold">{actionsLabel}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {pageRows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (renderActions ? 1 : 0)}
                className="p-6 text-center bg-card text-content-muted"
              >
                {emptyText}
              </td>
            </tr>
          ) : (
            pageRows.map((row, i) => (
              <tr
                key={row.id}
                className={`text-content ${i % 2 === 0 ? "bg-surface" : "bg-card"}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`p-2 ${cellAlign(c.align)}`}>
                    {c.render ? c.render(row) : String(row[c.key] ?? "")}
                  </td>
                ))}
                {renderActions && (
                  <td className="p-2 text-center">{renderActions(row)}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      {rows.length > pageSize && (
        <div className="mt-3 flex items-center justify-center gap-3 text-sm">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
            className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <span className="text-content-secondary">
            {safePage + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
            className="rounded-lg px-3 py-1 disabled:opacity-40 hover:bg-primary-500/10"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      )}
    </div>
  );
}
